import { useEffect, useState } from 'react';
import { useTheme } from 'styled-components';
import {
  BookmarkHeartContainer,
  BookmarkIconStyled,
  BookmarkText,
  BookmarkWrapper,
} from './Bookmark.styles';
import BookmarkCircles from './Bookmark.circles';
import useToggle from '~/hooks/useToggle';
import { triggerAnimation, updateBookmarks } from './Bookmark.helpers';
import { pushEvent, dataLayerEvents } from '~/helpers/datalayer';
import { useIntl, FormattedMessage } from 'react-intl';

/**
 * @param {BookmarkProps} props
 * @returns {React.ReactElement | null} The rendered component
 */
const Bookmark = ({
  id,
  showText,
  whiteVariant,
  onChange,
  company,
  jobId,
  jobTitle,
  context,
  city,
  country,
  dataLayerEventFilters,
  dataLayerSelectedFilters,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isHovered, toggleIsHovered] = useToggle(false);
  const [animate, toggleAnimate] = useToggle(false);

  function getBookmarks() {
    const local = localStorage?.getItem(theme.bookmarkName);
    if (local !== null && local.length > 0) {
      const existingBookmarks = JSON.parse(local);
      setIsBookmarked(!!existingBookmarks.find((b) => b === id));
    }
  }

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    window.addEventListener('storage', getBookmarks);
    window.dispatchEvent(new Event('storage'));
    return () => window.removeEventListener('storage', getBookmarks);
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  if (!theme.header.showFavourites) {
    return null;
  }

  const dataId = 'bookmark';
  const color = '#d5980c';

  /**
   * @type {Size}
   */
  const defaultIconSize = { height: 30, width: 30 };

  const commonEvent = {
    organization: company,
    location_city: city,
    location_country: country,
    job_id: jobId,
    job_title: jobTitle,
    context,
    selected_filters: dataLayerSelectedFilters,
    payload: dataLayerEventFilters,
  };

  function handleBookmarks() {
    if (onChange) {
      onChange();
    }
    const local = localStorage?.getItem(theme.bookmarkName);
    if (local !== null && local.length > 0) {
      const existingBookmarks = JSON.parse(local);

      if (existingBookmarks.find((b) => b === id)) {
        const filteredBookmarks = existingBookmarks.filter((b) => b !== id);
        updateBookmarks(filteredBookmarks, theme.bookmarkName);
        pushEvent({
          event: dataLayerEvents.JOB_MERKEN_REMOVE,
          ...commonEvent,
        });
      } else if (existingBookmarks.length <= theme.bookmarkLimit - 1) {
        updateBookmarks([...existingBookmarks, id], theme.bookmarkName);
        pushEvent({
          event: dataLayerEvents.JOB_MERKEN_ADD,
          ...commonEvent,
        });
      } else {
        window.dispatchEvent(new CustomEvent('bookmarklimit'));
      }
    } else {
      updateBookmarks([id], theme.bookmarkName);
      pushEvent({
        event: dataLayerEvents.JOB_MERKEN_ADD,
        ...commonEvent,
      });
    }
  }

  return (
    <BookmarkWrapper
      className="dontPrint"
      onClick={handleBookmarks}
      onMouseOver={toggleIsHovered}
      onMouseOut={toggleIsHovered}
      title={intl.formatMessage(
        { id: 'bookmarkAltText' },
        { bookmarked: isBookmarked ? 'true' : 'false' },
      )}
      data-id={`${dataId}-wrapper`}
      $defaultIconSize={defaultIconSize}
      $isBookmarked={isBookmarked}
    >
      <BookmarkHeartContainer
        data-id={`${dataId}-heart-container`}
        onClick={() => triggerAnimation({ isBookmarked, toggleAnimate })}
      >
        <BookmarkIconStyled
          icon="BOOKMARK"
          className={whiteVariant ? 'whiteVariant' : ''}
          checked={isBookmarked}
          dataId={`${dataId}-heart-icon`}
          color={color}
          parentHovered={isHovered}
          {...defaultIconSize}
        />
        <BookmarkCircles dataId={dataId} animate={animate} color={color} />
      </BookmarkHeartContainer>
      {showText && (
        <BookmarkText
          dataId={`${dataId}-text`}
          onClick={() => triggerAnimation({ isBookmarked, toggleAnimate })}
        >
          <FormattedMessage
            id="jobDetailBookmarkText"
            values={{
              bookmarked: isBookmarked ? 'true' : 'false',
            }}
          />
        </BookmarkText>
      )}
    </BookmarkWrapper>
  );
};

export default Bookmark;
